import React, { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { NumericFormat } from "react-number-format";
import getSymbolFromCurrency from "currency-symbol-map";

import GallerySlider from "components/GallerySlider/GallerySlider";
import StartRating from "components/StartRating/StartRating";
import SaleOffBadge from "components/SaleOffBadge/SaleOffBadge";
import Badge from "shared/Badge/Badge";
import { useClient } from "ClientContext";
import { useBooking } from "context/BookingContext";

//translation
import { useTranslation } from "react-i18next";

export interface ListingCardProps {
  className?: string;
  data?: any;
  size?: "default" | "small";
  IframeRequest?: {
    action: string;
    integrationType: string;
  };
}

const ListingCardComponent: FC<ListingCardProps> = ({
  size = "default",
  className = "",
  data,
  IframeRequest, // not currently used, but leaving in for future usage
}) => {
  // Destructure data props
  const {
    galleryImgs,
    listingCategory,
    address = {},
    bedrooms,
    bathrooms,
    saleOff,
    price,
    reviewStart,
    reviewCount,
    id,
    maxGuests,
    title,
    lodgifySiteApi,
    wixCmsApi
  } = data || {};

  const { t } = useTranslation();

    const { bookingState } = useBooking();
    const{ totalAvailabilities } = bookingState.searchResults;
    
    const isTotalPrice = totalAvailabilities > 0;

  // Global currency and integration type
  const { global_currency, global_currency_details, integration_TYPE,internal_ID } = useClient();
  const default_currency = global_currency?.[0] || "USD"; // fallback to USD if not found
  const currencySign = getSymbolFromCurrency(default_currency) || "$";
  const exchangeRate = global_currency_details?.euro_forex;
//console.log("lodgifySiteApi in listing card", default_currency);
  function convertCurrency(amount: number, exchangeRate: number): number {
    if (isNaN(amount) || isNaN(exchangeRate)) {
      throw new Error("Invalid input: Amount and exchange rate must be numbers.");
    }
    return parseFloat((amount * exchangeRate).toFixed(2)); // Round to 2 decimals
  }

  // Compute dynamic price only when integration_TYPE === "lodgify"
  const listingDynamicPrice_lodgify = useMemo(() => {
    if (integration_TYPE === "lodgify" && lodgifySiteApi?.min_price != null && exchangeRate != null) {
      return convertCurrency(lodgifySiteApi.min_price, exchangeRate);
    }
    return null;
  }, [integration_TYPE, lodgifySiteApi?.min_price, exchangeRate]);

  // Navigation
  const navigate = useNavigate();
  const handleCardClick = () => {
    if (id) {
      navigate(`/listings-list/${id}`);
    }
  };

  // Derived listing title
  const listingTitle = useMemo(() => {
    if (wixCmsApi?.title) {
      return wixCmsApi.title;
    }
    if (title) {
      return title;
    }
    return "No title";
  }, [wixCmsApi,lodgifySiteApi ,data.cmsItem, title]);

  // Slider Gallery
  const renderSliderGallery = () => (
    <div className="relative w-full">
      <GallerySlider
        uniqueID={`StayCard_${id}`}
        ratioClass="aspect-w-4 aspect-h-3"
        galleryImgs={galleryImgs}
        href={id}
      />
      {saleOff && <SaleOffBadge className="absolute left-3 top-3" />}
    </div>
  );

  // Content
  const renderContent = () => (
    <div className={size === "default" ? "p-4 space-y-4" : "p-3 space-y-2"}>
      {/* Icons row: guests, bedrooms, bathrooms */}
      <div className="space-y-2">
        <span className="flex items-center space-x-1.5 text-base font-medium text-neutral-500 dark:text-neutral-300">
          {(maxGuests > 0 || lodgifySiteApi) && (
            <>
              <i className="text-xl las la-user text-neutral-500"></i>
              <span className="text-md">{lodgifySiteApi ? lodgifySiteApi.max_people : maxGuests}</span>
            </>
          )}
          {bedrooms > 0 && (
            <>
              <i className="text-xl las la-bed text-neutral-500"></i>
              <span className="text-md">{bedrooms}</span>
            </>
          )}
          {bathrooms > 0 && (
            <>
              <i className="text-xl las la-bath text-neutral-500"></i>
              <span className="text-md">{bathrooms}</span>
            </>
          )}
        </span>

        {/* Badge + Title */}
        <div className="flex items-center space-x-2">
          {listingCategory && <Badge name={listingCategory} color="green" />}
          <h2 className={`font-medium capitalize ${size === "default" ? "text-lg" : "text-base"}`}>
            <span className="line-clamp-2">{listingTitle}</span>
          </h2>
        </div>

        {/* Location */}
        <div className="flex items-center text-neutral-500 dark:text-neutral-400 text-sm space-x-2">
          {size === "default" && (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          )}
          <span>
            {address.city},{address.state}
          </span>
        </div>
      </div>

      {/* Divider */}
      <div className="w-14 border-b border-neutral-100 dark:border-neutral-800"></div>

      {/* Price & Rating Row */}
      <div className="flex justify-between items-center">
        <span className="text-base font-semibold">
          <NumericFormat
            value={
              integration_TYPE === "lodgify" && listingDynamicPrice_lodgify !== null
                ? listingDynamicPrice_lodgify
                : price
            }
            displayType="text"
            thousandSeparator
            prefix={`${default_currency} ${currencySign}`}
            decimalScale={0}
          />
          {size === "default" && (
            <span className="text-sm text-neutral-500 dark:text-neutral-400 font-normal">
              {integration_TYPE ==="lodgify" && isTotalPrice ? ` ${t("Starting From")}` : `  ${t("Starting From")}`}
            </span>
          )}
        </span>
        {!!reviewStart && internal_ID !=="66945d50-54fc-45ea-9f53-6e2395f28c21" && <StartRating reviewCount={reviewCount} point={reviewStart} />}
      </div>
    </div>
  );

  return (
    <div
      className={`
        nc-StayCard 
        group 
        relative 
        drop-shadow-sm
        bg-white dark:bg-neutral-900 
        border border-neutral-100 dark:border-neutral-800 
        rounded-2xl 
        overflow-hidden 
        will-change-transform 
        hover:shadow-xl 
        transition-shadow 
        ${className}
      `}
      data-nc-id="StayCard"
      onClick={handleCardClick}
    >
      {renderSliderGallery()}
      {renderContent()}
    </div>
  );
};

const ListingCard = React.memo(ListingCardComponent);

export default ListingCard;